import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  GiftIcon,
  CalculatorIcon,
  XCircleIcon,
  PauseIcon,
  PlayIcon,
  ClockIcon,
} from "@heroicons/react/outline";
import PauseRequestModal from "../PendingPauseRequests/PauseRequestModal";
import {
  resumePlanThunk,
  getPlanByIdThunk,
  clearPauseData,
} from "../../../../store/thunks/jewelleryPlan/planThunks";
import { notifySuccess, notifyError } from "../../../../utils/notification";

const PlanActions = ({ plan }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [resumeLoading, setResumeLoading] = useState(false);

  const { planResumed, pauseError } = useSelector(
    (state) => state.jewelleryPlan?.plans || {}
  );

  const isPaused = plan?.pauseMetrics?.currentlyPaused;

  console.log(">>>> plan", { plan });

  // Handle successful resume
  useEffect(() => {
    if (planResumed) {
      notifySuccess("Plan resumed successfully!");
      setShowResumeModal(false);
      // Refresh plan data
      dispatch(getPlanByIdThunk(plan._id));
      setTimeout(() => {
        dispatch(clearPauseData());
      }, 2000);
    }
  }, [planResumed, dispatch, plan._id]);

  // Handle resume errors
  useEffect(() => {
    if (pauseError && showResumeModal) {
      notifyError(pauseError);
      setResumeLoading(false);
    }
  }, [pauseError, showResumeModal]);

  const canGenerateQuote = () => {
    return plan.status === "ACTIVE" || plan.status === "MATURED";
  };

  const canRedeem = () => {
    return (
      plan.status === "MATURED" ||
      (plan.status === "ACTIVE" && plan.paymentsDone >= 6)
    );
  };

  const canCancel = () => {
    return plan.status === "ACTIVE" || plan.status === "PAUSED";
  };

  const canPause = () => {
    return plan.status === "ACTIVE" && !isPaused;
  };

  const canResume = () => {
    // Can resume if currently paused AND there's a pause record to resume
    return (
      (isPaused || plan.status === "PAUSED") && getActivePauseInfo() !== null
    );
  };

  const handleGenerateQuote = () => {
    navigate(`/jewellery/${plan._id}/quote`);
  };

  const handleProcessRedemption = () => {
    navigate(`/jewellery/${plan._id}/redeem`);
  };

  const handleProcessCancelation = () => {
    navigate(`/jewellery/cancellations/${plan._id}/cancel`);
  };

  const handlePausePlan = () => {
    setShowPauseModal(true);
  };

  const handleResumePlan = () => {
    setShowResumeModal(true);
  };

  // ✅ NEW: Actual resume implementation
  const confirmResumePlan = async () => {
    // Find active pause
    const activePause = plan.pauseHistory?.find((p) => p.status === "ACTIVE");

    // if (!activePause) {
    //   notifyError("No active pause found for this plan");
    //   return;
    // }

    setResumeLoading(true);

    try {
      const result = await dispatch(
        resumePlanThunk({
          planId: plan._id,
          resumeData: {
            pauseId: activePause.pauseId,
            earlyResume: true,
            resumeDate: new Date().toISOString(),
          },
        })
      );

      if (resumePlanThunk.fulfilled.match(result)) {
        // Success handled by useEffect
      } else if (resumePlanThunk.rejected.match(result)) {
        notifyError(result.payload || "Failed to resume plan");
      }
    } catch (error) {
      notifyError(error.message || "Failed to resume plan");
    } finally {
      setResumeLoading(false);
    }
  };

  const handleCancelPlan = () => {
    if (cancelReason.trim()) {
      console.log("Cancelling plan:", plan._id, "Reason:", cancelReason);
      setShowCancelModal(false);
    }
  };

  // Get active pause details for display
  const getActivePauseInfo = () => {
    if (!isPaused) return null;

    // First try to find ACTIVE status
    let activePause = plan.pauseHistory?.find((p) => p.status === "ACTIVE");

    // If not found and plan is paused, maybe status wasn't updated properly
    // Look for the most recent pause (likely the one that's active)
    if (!activePause && isPaused) {
      // Get the most recent pause that isn't COMPLETED, REJECTED, or CANCELLED
      activePause = plan.pauseHistory
        ?.filter(
          (p) =>
            !["COMPLETED", "REJECTED", "CANCELLED", "EXPIRED"].includes(
              p.status
            )
        )
        .sort((a, b) => new Date(b.requestedAt) - new Date(a.requestedAt))[0];
    }

    return activePause;
  };

  const activePauseInfo = getActivePauseInfo();
  console.log(plan, resumeLoading, activePauseInfo);

  const actions = [
    {
      title: "Generate Quote",
      description: "Create a redemption quote for jewellery purchase",
      icon: CalculatorIcon,
      textColor: "text-secondary-dark",
      bgColor: "bg-secondary-light",
      action: handleGenerateQuote,
      enabled: canGenerateQuote(),
      requiredStatus: "Active or Matured",
    },
    {
      title: "Process Redemption",
      description: "Redeem the plan for jewellery",
      icon: GiftIcon,
      textColor: "text-success",
      bgColor: "bg-success-light",
      action: handleProcessRedemption,
      enabled: canRedeem(),
      requiredStatus: "Matured or 6+ payments",
    },
    {
      title: "Pause Plan",
      description: "Temporarily pause installment payments (1-3 months)",
      icon: PauseIcon,
      textColor: "text-white",
      bgColor: "bg-primary-light",
      action: handlePausePlan,
      enabled: canPause(),
      requiredStatus: "Active and not paused",
      show: canPause(),
    },
    {
      title: "Resume Plan",
      description: "Resume installment payments and continue plan",
      icon: PlayIcon,
      textColor: "text-success",
      bgColor: "bg-success-light",
      action: handleResumePlan,
      enabled: canResume(),
      requiredStatus: "Currently paused",
      show: canResume(),
    },
    {
      title: "Cancel Plan",
      description: "Cancel this savings plan",
      icon: XCircleIcon,
      textColor: "text-danger",
      bgColor: "bg-danger-light",
      action: handleProcessCancelation,
      enabled: canCancel(),
      requiredStatus: "Active plans only",
    },
  ];

  const visibleActions = actions.filter(
    (action) => action.show === undefined || action.show === true
  );

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-text mb-4">
          Available Actions
        </h3>
        <p className="text-sm text-neutral-dark">
          Perform actions based on the current plan status
        </p>
      </div>

      {/* Pause Status Alert */}
      {isPaused && activePauseInfo && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <ClockIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-yellow-900">
                Plan is Currently Paused
              </h4>
              <p className="text-sm text-yellow-800 mt-1">
                Paused until{" "}
                {new Date(activePauseInfo.endDate).toLocaleDateString()}. No
                installments are due during this period.
              </p>
              <div className="mt-2 text-xs text-yellow-700">
                <span className="font-medium">Reason:</span>{" "}
                {activePauseInfo.reason.replace(/_/g, " ")}
                <br />
                <span className="font-medium">Pause ID:</span>{" "}
                {activePauseInfo.pauseId}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pause Quota Information */}
      {!isPaused && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">
            Pause Plan Information
          </h4>
          <div className="grid grid-cols-3 gap-4 text-xs">
            <div>
              <p className="text-blue-600">Total Months Paused</p>
              <p className="text-lg font-semibold text-blue-900">
                {plan?.pauseMetrics?.totalMonthsPaused || 0} / 6
              </p>
            </div>
            <div>
              <p className="text-blue-600">Pause Instances Used</p>
              <p className="text-lg font-semibold text-blue-900">
                {plan?.pauseMetrics?.totalPauseInstances || 0} / 3
              </p>
            </div>
            <div>
              <p className="text-blue-600">Remaining Quota</p>
              <p className="text-lg font-semibold text-green-600">
                {plan?.pauseMetrics?.remainingPauseMonths || 6} months
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visibleActions.map((action, index) => (
          <div
            key={index}
            className={`border rounded-lg p-4 transition-all ${
              action.enabled
                ? "border-neutral-light hover:shadow-md cursor-pointer hover:border-primary"
                : "border-neutral-light bg-neutral-light cursor-not-allowed opacity-60"
            }`}
            onClick={action.enabled ? action.action : undefined}
          >
            <div className="flex items-start">
              <div className={`${action.bgColor} rounded-lg p-3 mr-4`}>
                <action.icon className={`h-6 w-6 ${action.textColor}`} />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-text">
                  {action.title}
                </h4>
                <p className="text-xs text-neutral-dark mt-1">
                  {action.description}
                </p>
                {!action.enabled && (
                  <p className="text-xs text-danger mt-2">
                    ⚠️ {action.requiredStatus}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Cancel Plan Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-neutral-dark bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-text mb-4">Cancel Plan</h3>
            <p className="text-sm text-neutral-dark mb-4">
              Are you sure you want to cancel this plan? This action cannot be
              undone.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-2">
                Cancellation Reason *
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                className="block w-full border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="Enter reason for cancellation..."
              />
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowCancelModal(false)}
                className="px-4 py-2 border border-neutral-light rounded-md text-sm font-medium text-text bg-background hover:bg-neutral-light"
              >
                Keep Plan Active
              </button>
              <button
                onClick={handleCancelPlan}
                disabled={!cancelReason.trim()}
                className={`px-4 py-2 rounded-md text-sm font-medium text-text-on-primary
                  ${
                    cancelReason.trim()
                      ? "bg-danger hover:bg-danger-dark"
                      : "bg-neutral-dark cursor-not-allowed"
                  }`}
              >
                Cancel Plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pause Request Modal */}
      <PauseRequestModal
        isOpen={showPauseModal}
        onClose={() => setShowPauseModal(false)}
        plan={plan}
      />

      {/* ✅ FIXED: Resume Plan Modal with actual API call */}
      {showResumeModal && (
        <div className="fixed inset-0 bg-neutral-dark bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-text mb-4">Resume Plan</h3>
            <p className="text-sm text-neutral-dark mb-4">
              Are you sure you want to resume this plan? Installment payments
              will restart from the next due date.
            </p>

            {activePauseInfo && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-blue-800 mb-2">
                  <span className="font-medium">Current Pause Details:</span>
                </p>
                <div className="text-xs text-blue-700 space-y-1">
                  <p>
                    • Pause ID: <strong>{activePauseInfo.pauseId}</strong>
                  </p>
                  <p>
                    • Started:{" "}
                    <strong>
                      {new Date(activePauseInfo.startDate).toLocaleDateString()}
                    </strong>
                  </p>
                  <p>
                    • Scheduled End:{" "}
                    <strong>
                      {new Date(activePauseInfo.endDate).toLocaleDateString()}
                    </strong>
                  </p>
                  <p>
                    • Duration:{" "}
                    <strong>{activePauseInfo.monthsPaused} month(s)</strong>
                  </p>
                </div>
              </div>
            )}

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-yellow-800">
                <span className="font-medium">Note:</span> Resuming early may
                adjust the maturity date. Your next installment will be due
                based on the updated schedule.
              </p>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowResumeModal(false)}
                disabled={resumeLoading}
                className="px-4 py-2 border border-neutral-light rounded-md text-sm font-medium text-text bg-background hover:bg-neutral-light disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmResumePlan}
                disabled={resumeLoading || !activePauseInfo}
                className={`px-4 py-2 rounded-md text-sm font-medium text-text-on-primary bg-success hover:bg-success-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center`}
              >
                {resumeLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Resuming...
                  </>
                ) : (
                  "Resume Plan"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanActions;
